(() => {
  if (window.customerReviewCarouselScriptLoaded) return;

  window.customerReviewCarouselScriptLoaded = true;

  class CustomerReviewCarousel {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-carousel-track]');
      this.previousButton = section.querySelector('[data-carousel-previous]');
      this.nextButton = section.querySelector('[data-carousel-next]');
      this.progress = section.querySelector('[data-carousel-progress]');

      if (!this.track) return;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

      this.scrollFrame = null;

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleTrackScroll = this.handleTrackScroll.bind(this);
      this.updateButtonState = this.updateButtonState.bind(this);

      this.previousButton?.addEventListener(
        'click',
        this.handlePreviousClick
      );

      this.nextButton?.addEventListener('click', this.handleNextClick);

      this.track.addEventListener('scroll', this.handleTrackScroll, {
        passive: true
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateButtonState);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.updateButtonState);
      }

      this.updateButtonState();
    }

    getScrollStep() {
      const firstSlide = this.track.querySelector('[data-carousel-slide]');

      if (!firstSlide) {
        return this.track.clientWidth;
      }

      const trackStyles = window.getComputedStyle(this.track);
      const gap =
        parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;

      return firstSlide.getBoundingClientRect().width + gap;
    }

    scrollCarousel(direction) {
      const distance = this.getScrollStep() * direction;

      this.track.scrollBy({
        left: distance,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    handlePreviousClick() {
      this.scrollCarousel(-1);
    }

    handleNextClick() {
      this.scrollCarousel(1);
    }

    handleTrackScroll() {
      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      this.scrollFrame = window.requestAnimationFrame(
        this.updateButtonState
      );
    }

    updateButtonState() {
      if (!this.track) return;

      const maximumScroll =
        this.track.scrollWidth - this.track.clientWidth;

      const currentScroll = Math.abs(this.track.scrollLeft);

      if (this.previousButton) {
        this.previousButton.disabled = currentScroll <= 2;
      }

      if (this.nextButton) {
        this.nextButton.disabled =
          currentScroll >= maximumScroll - 2 || maximumScroll <= 2;
      }

      if (this.progress) {
        const visibleRatio = Math.min(
          1,
          this.track.clientWidth / Math.max(1, this.track.scrollWidth)
        );

        const scrollRatio =
          maximumScroll > 2
            ? Math.min(1, currentScroll / maximumScroll)
            : 1;

        const progress =
          visibleRatio + scrollRatio * (1 - visibleRatio);

        this.progress.style.transform = `scaleX(${Math.min(
          1,
          Math.max(visibleRatio, progress)
        )})`;
      }
    }

    destroy() {
      this.previousButton?.removeEventListener(
        'click',
        this.handlePreviousClick
      );

      this.nextButton?.removeEventListener(
        'click',
        this.handleNextClick
      );

      this.track?.removeEventListener('scroll', this.handleTrackScroll);

      this.resizeObserver?.disconnect();

      window.removeEventListener('resize', this.updateButtonState);

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }
    }
  }

  const initializeCarousels = (container = document) => {
    const sections = [];

    if (container.matches?.('[data-customer-review-carousel]')) {
      sections.push(container);
    }

    container
      .querySelectorAll?.('[data-customer-review-carousel]')
      .forEach((section) => sections.push(section));

    sections.forEach((section) => {
      if (section.customerReviewCarouselInstance) return;

      section.customerReviewCarouselInstance =
        new CustomerReviewCarousel(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeCarousels(document);
    });
  } else {
    initializeCarousels(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeCarousels(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector?.(
      '[data-customer-review-carousel]'
    );

    const carouselSection = event.target.matches?.(
      '[data-customer-review-carousel]'
    )
      ? event.target
      : section;

    carouselSection?.customerReviewCarouselInstance?.destroy();

    if (carouselSection) {
      carouselSection.customerReviewCarouselInstance = null;
    }
  });
})();
